// UK Tax Year 2025/26
export const TAX = {
  personalAllowance: 12570,
  basic: { limit: 50270, rate: 0.20 },
  higher: { limit: 125140, rate: 0.40 },
  additional: { rate: 0.45 },
  ni: { class2weekly: 3.45, class4lower: 12570, class4upper: 50270, class4low: 0.09, class4high: 0.02 },
  homeOffice: { low: 10, mid: 18, high: 26 }, // per month
  mileage: { first: 0.45, after: 0.25, threshold: 10000 }
}

export function calcIncomeTax(profit) {
  if (profit <= TAX.personalAllowance) return 0
  const taxable = profit - TAX.personalAllowance
  if (profit <= TAX.basic.limit)
    return taxable * TAX.basic.rate
  const basic = (TAX.basic.limit - TAX.personalAllowance) * TAX.basic.rate
  if (profit <= TAX.higher.limit)
    return basic + (profit - TAX.basic.limit) * TAX.higher.rate
  return basic +
    (TAX.higher.limit - TAX.basic.limit) * TAX.higher.rate +
    (profit - TAX.higher.limit) * TAX.additional.rate
}

export function calcNI(profit) {
  const class2 = profit > TAX.ni.class4lower ? TAX.ni.class2weekly * 52 : 0
  const band1 = Math.max(0, Math.min(profit, TAX.ni.class4upper) - TAX.ni.class4lower)
  const band2 = Math.max(0, profit - TAX.ni.class4upper)
  return class2 + band1 * TAX.ni.class4low + band2 * TAX.ni.class4high
}

export function calcTax(income, expenses) {
  const profit = Math.max(0, income - expenses)
  const incomeTax = calcIncomeTax(profit)
  const ni = calcNI(profit)
  return { profit, incomeTax, ni, total: incomeTax + ni }
}

export function mileageAllowance(miles) {
  if (miles <= TAX.mileage.threshold) return miles * TAX.mileage.first
  return TAX.mileage.threshold * TAX.mileage.first + (miles - TAX.mileage.threshold) * TAX.mileage.after
}

export function homeOfficeAllowance(months, hoursPerMonth) {
  const rate = hoursPerMonth >= 101 ? TAX.homeOffice.high : hoursPerMonth >= 51 ? TAX.homeOffice.mid : TAX.homeOffice.low
  return months * rate
}

export function annualise(quarterlyProfit, quartersCompleted) {
  if (quartersCompleted === 0) return 0
  return (quarterlyProfit / quartersCompleted) * 4
}

export function fmt(n) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n)
}

export function fmtFull(n) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n)
}

// Quarterly Score: 0-100
export function calcScore({ categorisedPct, receiptsUploaded, taxPotFunded, submissionsOnTime }) {
  let score = 0
  score += categorisedPct * 0.35        // 35pts: are txns categorised?
  score += receiptsUploaded * 0.25      // 25pts: receipts uploaded
  score += taxPotFunded * 0.25          // 25pts: tax pot set aside
  score += submissionsOnTime * 0.15     // 15pts: on time submissions
  return Math.round(Math.min(100, score))
}

export function scoreLabel(score) {
  if (score >= 80) return { label: 'Great', cls: 'score-great' }
  if (score >= 50) return { label: 'On Track', cls: 'score-ok' }
  return { label: 'Needs Attention', cls: 'score-low' }
}

// MTD ITSA 2026/27 — first mandated year
// Deadlines are the 7th of the month following each quarter end
export function getDeadlines() {
  return [
    { q: 'Q1',  period: '6 Apr – 5 Jul 2026',  deadline: '7 Aug 2026',  date: new Date('2026-08-07'), type: 'submission', label: 'Q1 quarterly update' },
    { q: 'Q2',  period: '6 Jul – 5 Oct 2026',  deadline: '7 Nov 2026',  date: new Date('2026-11-07'), type: 'submission', label: 'Q2 quarterly update' },
    { q: 'Q3',  period: '6 Oct – 5 Jan 2027',  deadline: '7 Feb 2027',  date: new Date('2027-02-07'), type: 'submission', label: 'Q3 quarterly update' },
    { q: 'Q4',  period: '6 Jan – 5 Apr 2027',  deadline: '7 May 2027',  date: new Date('2027-05-07'), type: 'submission', label: 'Q4 quarterly update' },
    { q: 'EOY', period: 'Full year 2026/27',    deadline: '31 Jan 2028', date: new Date('2028-01-31'), type: 'payment',    label: 'Final tax return & payment' },
    { q: 'POA1', period: 'Payment on Account',  deadline: '31 Jan 2027', date: new Date('2027-01-31'), type: 'payment',    label: '1st payment on account' },
    { q: 'POA2', period: 'Payment on Account',  deadline: '31 Jul 2027', date: new Date('2027-07-31'), type: 'payment',    label: '2nd payment on account' },
  ].sort((a, b) => a.date - b.date)
}

export function getCurrentQuarter() {
  const now = new Date()
  const m = now.getMonth() + 1
  // 2026/27 MTD ITSA quarters
  if (m >= 4 && m <= 6)  return { q: 1, label: 'Q1', start: '6 Apr 2026',  end: '5 Jul 2026',  deadline: '7 Aug 2026',  taxYear: '2026/27' }
  if (m >= 7 && m <= 9)  return { q: 2, label: 'Q2', start: '6 Jul 2026',  end: '5 Oct 2026',  deadline: '7 Nov 2026',  taxYear: '2026/27' }
  if (m >= 10 && m <= 12) return { q: 3, label: 'Q3', start: '6 Oct 2026',  end: '5 Jan 2027',  deadline: '7 Feb 2027',  taxYear: '2026/27' }
  return                         { q: 4, label: 'Q4', start: '6 Jan 2027',  end: '5 Apr 2027',  deadline: '7 May 2027',  taxYear: '2026/27' }
}

export function getTaxYear(dateStr) {
  const d = new Date(dateStr)
  const y = d.getFullYear()
  const m = d.getMonth() + 1
  // UK tax year: Apr 6 to Apr 5
  if (m > 4 || (m === 4 && d.getDate() >= 6)) {
    return `${y}-${String(y + 1).slice(2)}`
  }
  return `${y - 1}-${String(y).slice(2)}`
}
